import type { Point } from "zxing-wasm/reader";

export type EnhancementMode = "off" | "auto" | "aggressive";

export interface ScanResult {
  /** Decoded text content. */
  text: string;
  /** Raw bytes of the payload. */
  bytes: Uint8Array;
  /** SDK format id, e.g. "qrcode", "ean13". */
  format: string;
  /** Corner points of the symbol in *video pixel* coordinates. */
  position: {
    topLeft: Point;
    topRight: Point;
    bottomLeft: Point;
    bottomRight: Point;
  };
  /** Whether this frame went through the enhancement pipeline before decoding. */
  enhanced: boolean;
  /** Timestamp (performance.now()) the result was produced. */
  timestamp: number;
}

export interface ScanSettings {
  /** Enabled format ids, see formats.ts. */
  enabledFormats: string[];
  /** Keep scanning and collecting unique results instead of stopping at the first hit. */
  multipleScanning: boolean;
  /** Show the corner-bracket viewfinder overlay. */
  viewFinder: boolean;
  /** Play a beep sound on every successful decode. */
  beepTone: boolean;
  /** Draw a live bounding-box overlay on top of the recognized symbol. */
  arOverlay: boolean;
  /** Low-light / glare mitigation strategy, see enhance/. */
  enhancement: EnhancementMode;
  /** "auto" lets the SDK turn the torch on under sustained low light. */
  torch: "auto" | "on" | "off";
}

export const DEFAULT_SETTINGS: ScanSettings = {
  enabledFormats: [],
  multipleScanning: false,
  viewFinder: true,
  beepTone: false,
  arOverlay: false,
  enhancement: "auto",
  torch: "auto",
};

export interface FrameQuality {
  /** 0..255 mean luma of the sampled region. */
  meanLuma: number;
  /** Fraction (0..1) of pixels considered blown-out / specular glare. */
  glareFraction: number;
  /** Fraction (0..1) of pixels considered near-black / underexposed. */
  shadowFraction: number;
  /** Laplacian-variance based sharpness score, higher = sharper. */
  focusScore: number;
  /** Heuristic verdict used to pick a decode strategy. */
  condition: "good" | "low-light" | "glare" | "blurry" | "mixed";
}

export type ScannerEventMap = {
  scan: ScanResult;
  scanBatch: ScanResult[];
  error: Error;
  start: void;
  stop: void;
  frameQuality: FrameQuality;
  torch: boolean;
  home: void;
  settingsOpen: void;
  settingsClose: void;
  settingsChange: ScanSettings;
};
